import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { MdSave, MdDelete } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { InteractiveSpan } from '@Components/InteractiveEl/InteractiveEl';
import { Appearance, Design, Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { AnomalyDetectionLambdaFetch, getUserFacingMessage } from '@src/utils/fetch-utils';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { emailSelector } from '@src/redux/user';
import './EditAlertModal.scss';
import { AlertFormData, DEFAULT_ALERT_FORM_DATA } from './anomalyDetectionConstants';
import {
  hasValidNonEmptyStrings,
  validateMonitoringType,
  validateNotificationMethods
} from './utils/alertUtils';
import { useEmailAddresses, useSlackMembers, useSlackChannels } from './hooks/useAlertData';
import { PresetForm } from './components/PresetForm';

interface CreatePresetModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: () => void;
}

export const CreatePresetModal = ({ show, onHide, onSubmit }: CreatePresetModalProps) => {
  const company = useAppSelector(currentCompanySelector);
  const email = useAppSelector(emailSelector);

  const {
    data: emailAddressOptions = [],
    isLoading: isLoadingEmailAddresses,
    error: emailAddressError,
    refetch: refetchEmails
  } = useEmailAddresses();

  const {
    data: slackMemberOptions = [],
    isLoading: isLoadingSlackMembers,
    error: slackMembersError,
    refetch: refetchSlackMembers
  } = useSlackMembers();

  const {
    data: slackChannelOptions = [],
    isLoading: isLoadingSlackChannels,
    error: slackChannelsError,
    refetch: refetchSlackChannels
  } = useSlackChannels();

  const [formData, setFormData] = useState<AlertFormData>(DEFAULT_ALERT_FORM_DATA);
  const [presetName, setPresetName] = useState('');
  const [description, setDescription] = useState('');
  const setError = useSetError();
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  useEffect(() => {
    if (show) {
      setFormData(DEFAULT_ALERT_FORM_DATA);
      setPresetName('');
      setDescription('');
      setShowSuccessPopup(false);
      setHasAttemptedSave(false);

      refetchEmails();
      refetchSlackMembers();
      refetchSlackChannels();
    }
  }, [show, refetchEmails, refetchSlackMembers, refetchSlackChannels]);

  const isFormValid = (): boolean => {
    if (!presetName.trim()) {
      return false;
    }

    if (!description.trim()) {
      return false;
    }

    if (validateMonitoringType(formData) !== null) {
      return false;
    }

    if (formData.actual.enabled) {
      const actualUnder = formData.actual.under.replace(/[^0-9]/g, '');
      const actualOver = formData.actual.over.replace(/[^0-9]/g, '');
      if (!actualUnder || !actualOver) {
        return false;
      }
    }

    if (formData.projected.enabled) {
      const projectedUnder = formData.projected.under.replace(/[^0-9]/g, '');
      const projectedOver = formData.projected.over.replace(/[^0-9]/g, '');
      if (!projectedUnder || !projectedOver) {
        return false;
      }
    }

    if (formData.remainder.enabled) {
      const remainderUnder = formData.remainder.under.replace(/[^0-9]/g, '');
      const remainderOver = formData.remainder.over.replace(/[^0-9]/g, '');
      if (!remainderUnder || !remainderOver) {
        return false;
      }
    }

    if (!formData.email.enabled && !formData.slack.enabled) {
      return false;
    }

    if (formData.slack.enabled) {
      if (
        !hasValidNonEmptyStrings(formData.slack.channels) ||
        !hasValidNonEmptyStrings(formData.slack.members)
      ) {
        return false;
      }
    }

    if (formData.email.enabled) {
      if (!hasValidNonEmptyStrings(formData.email.addresses)) {
        return false;
      }
    }

    return true;
  };

  const getFieldValidationErrors = (): Record<string, string> => {
    const fieldErrors: Record<string, string> = {};

    if (!presetName.trim()) {
      fieldErrors.presetName = 'Preset name is required';
    }

    if (!description.trim()) {
      fieldErrors.description = 'Description is required';
    }

    const monitoringTypeError = validateMonitoringType(formData);
    if (monitoringTypeError) {
      fieldErrors.monitoringType = monitoringTypeError;
    }

    if (formData.actual.enabled) {
      const actualUnder = formData.actual.under.replace(/[^0-9]/g, '');
      const actualOver = formData.actual.over.replace(/[^0-9]/g, '');
      if (!actualUnder) {
        fieldErrors.actualUnder = "Actual 'Under' threshold is required";
      }
      if (!actualOver) {
        fieldErrors.actualOver = "Actual 'Over' threshold is required";
      }
    }

    if (formData.projected.enabled) {
      const projectedUnder = formData.projected.under.replace(/[^0-9]/g, '');
      const projectedOver = formData.projected.over.replace(/[^0-9]/g, '');
      if (!projectedUnder) {
        fieldErrors.projectedUnder = "Projected 'Under' threshold is required";
      }
      if (!projectedOver) {
        fieldErrors.projectedOver = "Projected 'Over' threshold is required";
      }
    }

    if (formData.remainder.enabled) {
      const remainderUnder = formData.remainder.under.replace(/[^0-9]/g, '');
      const remainderOver = formData.remainder.over.replace(/[^0-9]/g, '');
      if (!remainderUnder) {
        fieldErrors.remainderUnder = "Remainder 'Under' threshold is required";
      }
      if (!remainderOver) {
        fieldErrors.remainderOver = "Remainder 'Over' threshold is required";
      }
    }

    const notificationErrors = validateNotificationMethods(formData);
    Object.assign(fieldErrors, notificationErrors);

    return fieldErrors;
  };

  const handleSave = async () => {
    setHasAttemptedSave(true);
    if (isFormValid()) {
      try {
        setIsSaving(true);

        const presetData = {
          company,
          alert_type: 'Budget',
          preset_name: presetName,
          description,
          preset_data: formData,
          created_by_email: email
        };

        await AnomalyDetectionLambdaFetch('/company-alert-presets', {
          method: 'POST',
          body: presetData
        });

        setShowSuccessPopup(true);
        onSubmit();
      } catch (error) {
        setError({
          title: "Couldn't create preset",
          message: getUserFacingMessage(error, "Couldn't create preset"),
          reportError: error
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCancel = () => {
    onHide();
  };

  const handleSuccessClose = () => {
    setShowSuccessPopup(false);
    onHide();
    onSubmit();
  };

  const handleFormDataChange = (newFormData: AlertFormData) => {
    setFormData(newFormData);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size='xl' centered className='edit-alert-modal'>
        <Modal.Header>
          <Modal.Title>Create Budget Preset</Modal.Title>
          <div className='required-fields-note'>*Required fields marked with an asterisk.</div>
        </Modal.Header>

        <Modal.Body>
          <PresetForm
            formData={formData}
            onFormDataChange={handleFormDataChange}
            presetName={presetName}
            onPresetNameChange={setPresetName}
            description={description}
            onDescriptionChange={setDescription}
            emailAddressOptions={emailAddressOptions}
            slackMemberOptions={slackMemberOptions}
            slackChannelOptions={slackChannelOptions}
            isLoadingEmailAddresses={isLoadingEmailAddresses}
            isLoadingSlackMembers={isLoadingSlackMembers}
            isLoadingSlackChannels={isLoadingSlackChannels}
            emailAddressError={emailAddressError}
            slackMembersError={slackMembersError}
            slackChannelsError={slackChannelsError}
            fieldErrors={hasAttemptedSave ? getFieldValidationErrors() : {}}
          />
        </Modal.Body>

        <Modal.Footer className='modal-controls'>
          <div className='modal-footer-left'>
            <InteractiveSpan className='delete-icon' onClick={handleCancel} aria-label='Delete'>
              <MdDelete size={25} />
            </InteractiveSpan>
          </div>
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={handleCancel}>
            Cancel
          </Button>

          <div className='modal-footer-right'>
            <Button
              onClick={handleSave}
              leadingElement={<MdSave />}
              disabled={isSaving || (hasAttemptedSave && !isFormValid())}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            {hasAttemptedSave && !isFormValid() && (
              <div className='validation-error-message'>Please fill out all required fields.</div>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSuccessPopup}
        onHide={handleSuccessClose}
        size='sm'
        centered
        className='success-popup-modal'
      >
        <Modal.Body className='text-center p-4'>
          <div className='success-icon mb-3'>
            <MdSave className='success-icon-svg' />
          </div>
          <h5 className='mb-3'>Preset Created Successfully!</h5>
          <p className='text-muted mb-4'>
            Your new budget preset &quot;{presetName}&quot; has been created and is now available
            for use.
          </p>
          <Button onClick={handleSuccessClose} size={Size.SM}>
            Continue
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};
